import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-gray-50 font-sans">
    <nav className="bg-white shadow-sm">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <Link to="/" className="text-xl font-bold text-brand-navy">
            3PL<span className="text-brand-gold">+</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-body hover:text-brand-navy">
            Back to Home
          </Link>
        </div>
      </div>
    </nav>

    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-3xl font-bold text-brand-navy">Privacy Policy</h1>
      <p className="mt-2 text-sm text-gray-500">Last updated: March 2026</p>

      <div className="mt-8 prose prose-gray max-w-none space-y-6 text-body">
        <h2 className="text-xl font-semibold text-brand-navy">1. Information We Collect</h2>
        <p>
          When you use our 3PL+ platform or submit a contact form, we may collect the following information:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information:</strong> Name, email address, phone number, and company name provided during account setup.</li>
          <li><strong>Inventory data:</strong> Product descriptions, SKUs, serial numbers, quantities, and related logistics data that you provide as part of our warehousing services.</li>
          <li><strong>Contact form submissions:</strong> Name, email, phone number, company, and message content submitted through our website contact form.</li>
          <li><strong>Usage data:</strong> Login timestamps, IP addresses, and browser information collected automatically for security and system monitoring purposes.</li>
        </ul>

        <h2 className="text-xl font-semibold text-brand-navy">2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide and manage our warehousing, fulfillment, and logistics services.</li>
          <li>Maintain and secure your account and the platform.</li>
          <li>Generate invoices and manage billing for services rendered.</li>
          <li>Respond to your inquiries and contact form submissions.</li>
          <li>Send service-related notifications such as request status updates and invoice notices.</li>
        </ul>

        <h2 className="text-xl font-semibold text-brand-navy">3. Data Sharing</h2>
        <p>
          We do not sell, trade, or share your personal information or inventory data with third parties. Your data is used exclusively for providing our services to you. Specifically:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your data is not shared with other customers. Each customer's data is fully isolated within our system.</li>
          <li>We do not share your data with advertising networks, data brokers, or analytics providers.</li>
          <li>We may use third-party service providers (such as email delivery services) to send transactional emails on our behalf. These providers only receive the minimum information necessary to deliver the email.</li>
        </ul>

        <h2 className="text-xl font-semibold text-brand-navy">4. Data Storage and Security</h2>
        <p>
          Your data is stored in secure, encrypted databases. We use industry-standard security measures including:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Encrypted connections (SSL/TLS) for all data in transit.</li>
          <li>Encrypted database storage for sensitive information.</li>
          <li>Password hashing using bcrypt. We never store plaintext passwords.</li>
          <li>Role-based access controls to ensure only authorized personnel can access your data.</li>
          <li>Rate limiting and brute-force protection on authentication endpoints.</li>
        </ul>

        <h2 className="text-xl font-semibold text-brand-navy">5. Data Retention</h2>
        <p>
          We retain your account and inventory data for as long as you are an active customer. If you discontinue services, we retain records for a reasonable period to support any outstanding billing or legal obligations, after which your data is securely deleted.
        </p>

        <h2 className="text-xl font-semibold text-brand-navy">6. Your Rights</h2>
        <p>
          You have the right to access, correct, or request deletion of your personal information at any time. Contact us to make a request and we will respond within a reasonable timeframe.
        </p>

        <h2 className="text-xl font-semibold text-brand-navy">7. Changes to This Policy</h2>
        <p>
          We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date.
        </p>

        <h2 className="text-xl font-semibold text-brand-navy">8. Contact</h2>
        <p>
          If you have questions about this privacy policy or how your data is handled, please <Link to="/#contact" className="text-brand-gold hover:underline">contact us</Link>.
        </p>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
